import TermsModal from "@/components/modal/TermsModal";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { useQuoteContext } from "@/contexts/quote-context";
import { copyToClipboard } from "@/utils/copy-clipboard";
import { formatMoneyBr } from "@/utils/format";
import { cleanApiUrl, openInNewTab } from "@/utils/url-helpers";
import * as Sentry from "@sentry/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FaCopy, FaExternalLinkAlt } from "react-icons/fa";
import { FaPix, FaQrcode } from "react-icons/fa6";
import QRCode from "react-qr-code";
import CouponComponent from "./coupon-component";
import type { PaymentFormValues } from "./payment-types";
import { PersonalInfoDisplay } from "./personal-info-display";

interface PixPaymentProps {
  setGeneratedPix: (value: boolean) => void;
  generatedPix: boolean;
  onNextStep: () => void;
}

type PaymentError = Error & {
  rawBackendResponse?: unknown;
  httpStatus?: number;
  needsVerification?: boolean;
};

export default function PixPayment({
  setGeneratedPix,
  generatedPix,
  onNextStep,
}: PixPaymentProps) {
  const {
    payWithPix,
    refetchTransaction,
    checkPaymentStatus,
    transactionId,
    quotePrice,
    transaction,
    cotacao,
    persistedPixData,
    savePixData,
  } = useQuoteContext();
  const form = useFormContext<PaymentFormValues>();
  const [timer, setTimer] = useState(900);
  const [loading, setLoading] = useState(false);
  const [isTermsModalOpen, setTermsModalOpen] = useState(false);

  useEffect(() => {
    if (!generatedPix) return;
    if (timer === 0) return;

    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [generatedPix, timer]);

  useEffect(() => {
    const pixFatura = transaction.find(
      (f) =>
        f.por_descricao.toLowerCase() === "pix" &&
        f.tts_descricao.toLowerCase() !== "vencido",
    );

    if (pixFatura && !persistedPixData) {
      setGeneratedPix(true);

      const cleanedQrCode = cleanApiUrl(pixFatura.qrcode);
      const cleanedQrCodeText = cleanApiUrl(pixFatura.qrcode_text);
      const cleanedSecureUrl = cleanApiUrl(pixFatura.cre_url_link);

      savePixData({
        success: true,
        data: {
          cre_codigo: pixFatura.cre_codigo,
          invoice_id: pixFatura.cre_codigo,
          due_date: pixFatura.cre_data_vencimento,
          secure_url: cleanedSecureUrl,
          pix: {
            qrcode: cleanedQrCode,
            qrcode_text: cleanedQrCodeText,
            status: "pending",
          },
        },
      });
    }
  }, [transaction, persistedPixData, savePixData, setGeneratedPix]);

  const formattedTimer = `${Math.floor(timer / 60)
    .toString()
    .padStart(2, "0")}:${(timer % 60).toString().padStart(2, "0")}`;

  const handleCopyCode = () => {
    const code = persistedPixData?.data.pix.qrcode_text;
    if (!code || code.trim().length === 0) {
      toast({
        title: "Código não disponível",
        description: "Não foi possível copiar o código PIX.",
        variant: "destructive",
      });
      return;
    }
    copyToClipboard(code, "Código PIX copiado!");
  };

  const handleGenerateQrCode = async () => {
    // Proteção: se já tem dados persistidos, não gerar nova cobrança
    if (persistedPixData) {
      setGeneratedPix(true);
      return;
    }

    const isValid = await form.trigger(["termsAccepted"]);

    if (!isValid) return;

    setLoading(true);

    const values = form.getValues();

    try {
      const pixResponse = await payWithPix(values);
      savePixData(pixResponse);
      setGeneratedPix(true);
    } catch (err: unknown) {
      const paymentError = err as PaymentError;

      if (paymentError?.needsVerification !== false) {
        toast({
          title: "Verificando pagamento...",
          description: "Aguarde, estamos verificando se o PIX foi gerado.",
        });
        const faturas = await refetchTransaction();
        const pixFatura = faturas.find(
          (f) =>
            f.por_descricao.toLowerCase() === "pix" &&
            f.tts_descricao.toLowerCase() !== "vencido",
        );
        if (pixFatura) {
          setGeneratedPix(true);
          return;
        }
      }

      const description =
        paymentError?.message ||
        "Tente novamente mais tarde ou verifique as informações cadastradas";
      toast({
        title: "Erro ao pagar com PIX",
        description,
        variant: "destructive",
      });
      Sentry.withScope((scope) => {
        scope.setTag("payment_method", "pix");
        scope.setContext("payment_error", {
          userMessage: description,
          cotacaoId: cotacao?.cve_codigo,
          clientEmail: cotacao?.ctc_email,
          clientPhone: cotacao?.ctc_telefone_1,
          backendResponse: paymentError.rawBackendResponse,
          httpStatus: paymentError.httpStatus,
        });
        Sentry.captureException(paymentError);
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!generatedPix || !transactionId) return;

    const interval = setInterval(async () => {
      try {
        const status = await checkPaymentStatus(transactionId);
        if (status === "approved") {
          clearInterval(interval);
          onNextStep();
        }
      } catch (error) {
        console.error("Erro ao checar status do pagamento:", error);
      }
    }, 15000);

    return () => clearInterval(interval);
  }, [generatedPix, transactionId, checkPaymentStatus, onNextStep]);

  return (
    <div className="space-y-6">
      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setTermsModalOpen(false)}
      />
      <div className="space-y-4">
        <h3 className="text-2xl font-semibold text-n-green">PIX</h3>

        <div className="space-y-2">
          <p className="font-bold  text-n-text-neutral mb-2">
            Valor total:{" "}
            <span className="">{formatMoneyBr(quotePrice.total)}</span>
          </p>

          <CouponComponent />
        </div>

        {!generatedPix && (
          <>
            <div className="mt-4 border-t pt-4">
              <h4 className="font-bold mb-2 text-n-text-neutral">
                É rápido e prático. Veja como é fácil:
              </h4>
            </div>

            <div className="mt-4">
              <h4 className="font-medium mb-3">Dados do titular:</h4>
              <PersonalInfoDisplay />
            </div>

            <div className="space-y-4 mt-6">
              <FormField
                control={form.control}
                name="termsAccepted"
                render={({ field }) => (
                  <FormItem className="flex items-start justify-start gap-2 mt-6">
                    <FormControl>
                      <Checkbox
                        className="mt-2"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        id="terms-bank"
                      />
                    </FormControl>
                    <div className="flex-1 flex flex-col">
                      <Label
                        htmlFor="terms-bank"
                        className="text-sm text-n-text mb-2"
                      >
                        Declaro que li e concordo com os{" "}
                        <span
                          className="text-n-primary font-medium cursor-pointer underline hover:text-n-primary/80"
                          onClick={() => setTermsModalOpen(true)}
                        >
                          Termos e Condições
                        </span>{" "}
                        de serviço
                      </Label>
                      <FormMessage />
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </>
        )}

        {generatedPix ? (
          <div>
            <div className="flex flex-col items-center space-y-4 md:py-4 py-2">
              <div className="bg-n-bg-1 p-2 border rounded-lg">
                {persistedPixData?.data.pix.qrcode_text ? (
                  <QRCode
                    value={persistedPixData.data.pix.qrcode_text}
                    size={192}
                    className="mx-auto"
                  />
                ) : (
                  <div className="w-48 h-48 bg-gray-200 animate-pulse rounded-lg" />
                )}
              </div>

              <div className="w-full">
                <p className="text-sm text-n-text-neutral mb-2 text-center">
                  Ou copie o código PIX abaixo:
                </p>
                <div className="flex items-center">
                  <Input
                    value={persistedPixData?.data.pix.qrcode_text || ""}
                    readOnly
                    className="bg-gray-50 text-n-text-neutral text-xs rounded-r-none"
                  />
                  <Button
                    type="button"
                    className="ml-1 rounded-r-lg rounded-l-none md:h-[48px] h-[44px]"
                    onClick={handleCopyCode}
                  >
                    <FaCopy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {persistedPixData?.data.secure_url && (
              <div className="mt-4">
                <Button
                  type="button"
                  className="w-full rounded-md text-sm"
                  onClick={() =>
                    openInNewTab(
                      persistedPixData?.data.secure_url,
                      "Não foi possível abrir o PIX.",
                    )
                  }
                >
                  Abrir PIX em outra aba
                  <FaExternalLinkAlt className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
              <h4 className="font-medium text-yellow-700 mb-2">Instruções:</h4>
              <ol className="list-decimal list-inside space-y-2 text-sm text-n-text">
                <li>Abra o aplicativo do seu banco</li>
                <li>Escolha a opção de pagamento via PIX</li>
                <li>Escaneie o QR Code ou cole o código copiado</li>
                <li>Confirme as informações e finalize o pagamento</li>
                <li>
                  O pagamento será confirmado automaticamente em até 5 minutos
                </li>
              </ol>
            </div>
            {generatedPix && (
              <p className="text-sm text-n-red mt-2 text-center">
                QR Code expira em {formattedTimer}
              </p>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-4 md:py-6 py-2">
            <div className="bg-n-bg-1 p-6 border rounded-lg w-full flex flex-col items-center">
              <FaPix className="h-16 w-16 text-gray-400 mb-4" />
              <p className="text-sm text-n-text-neutral mb-4  text-center">
                Clique em "Gerar QR Code" para gerar o QR Code para pagamento
                via PIX
              </p>
              <Button
                type="submit"
                className="flex items-center gap-2"
                onClick={handleGenerateQrCode}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Gerando
                  </>
                ) : (
                  <>
                    <FaQrcode className="h-4 w-4 mr-2" />
                    Gerar QR Code
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
